import { useMemo } from 'react'
import { Plus, Layers, Trash2 } from 'lucide-react'
import { formatCurrency } from '@/utils/format-currency'
import { cn } from '@/utils/cn'
import type { PropostaItem } from '@/types'
import type { ComboData, ComboValue } from './types'

interface ComboSectionProps {
  itens: PropostaItem[]
  combos: ComboData[]
  comboValues: ComboValue[]
  canEdit: boolean
  onAddCombo: () => void
  onToggleComboItem: (comboId: string, itemId: string) => void
  onComboDesconto: (comboId: string, val: number) => void
  onRemoveCombo: (comboId: string) => void
  onSetComboFinalPrice: (comboGroupId: string, price: number) => void
}

export function ComboSection({
  itens,
  combos,
  comboValues,
  canEdit,
  onAddCombo,
  onToggleComboItem,
  onComboDesconto,
  onRemoveCombo,
  onSetComboFinalPrice,
}: ComboSectionProps) {
  const comboPricingData = useMemo(() => {
    const comboGroups = new Map<string, PropostaItem[]>()
    for (const item of itens) {
      if (!item.comboGroupId) continue
      if (!comboGroups.has(item.comboGroupId)) comboGroups.set(item.comboGroupId, [])
      comboGroups.get(item.comboGroupId)!.push(item)
    }

    const result: {
      groupId: string
      label: string
      items: PropostaItem[]
      subtotal: number
      comboFinalPrice: number
      desconto: number
    }[] = []

    let comboIndex = 1
    for (const [groupId, groupItems] of comboGroups) {
      // comboGroupId no catálogo (PlanSelector) é preço de pacote — só exibir com 2+ itens
      if (groupItems.length < 2) continue
      const groupSubtotal = groupItems.reduce((acc, i) => acc + i.total, 0)
      const groupFinalPrice = groupItems[0].comboFinalPrice ?? groupSubtotal
      result.push({
        groupId,
        label: `Combo ${comboIndex}`,
        items: groupItems,
        subtotal: groupSubtotal,
        comboFinalPrice: groupFinalPrice,
        desconto: groupSubtotal - groupFinalPrice,
      })
      comboIndex++
    }
    return result
  }, [itens])

  // Combo de desconto só faz sentido com 2+ itens na proposta em edição
  if (itens.length < 2 && comboPricingData.length === 0) return null

  return (
    <>
      {/* Combo Management */}
      <div className="space-y-3 pt-2 border-t border-white/[0.06]">
        <div className="flex items-center justify-between">
          <h4 className="text-[11px] font-semibold text-text-label uppercase tracking-wider flex items-center gap-2">
            <Layers size={12} />
            Combos
          </h4>
          {itens.length > 0 && (
            <button
              onClick={onAddCombo}
              className="text-[10px] text-accent hover:underline flex items-center gap-1"
            >
              <Plus size={10} />
              Novo Combo
            </button>
          )}
        </div>

        {combos.length === 0 && (
          <p className="text-xs text-text-label italic">
            Opcional: crie um combo para agrupar itens e aplicar desconto antes de salvar.
          </p>
        )}

        {combos.map((combo) => {
          const cv = comboValues.find((c) => c.id === combo.id)
          return (
            <div
              key={combo.id}
              className="p-3 rounded-md bg-white/[0.03] border border-white/[0.06] space-y-2"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-text-primary">{combo.nome}</span>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] text-text-label">Desc.:</span>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={combo.desconto}
                      onChange={(e) => onComboDesconto(combo.id, Number(e.target.value))}
                      className="w-12 bg-white/[0.04] border border-white/[0.06] text-text-primary text-xs rounded px-1.5 py-0.5 text-center outline-none focus:border-accent"
                    />
                    <span className="text-[10px] text-text-label">%</span>
                  </div>
                  <button
                    onClick={() => onRemoveCombo(combo.id)}
                    className="text-text-muted hover:text-red-400 transition-colors"
                  >
                    <Trash2 size={11} />
                  </button>
                </div>
              </div>

              <div className="space-y-1">
                {itens.map((item) => {
                  const isSelected = combo.itemIds.includes(item.id)
                  return (
                    <button
                      key={item.id}
                      onClick={() => onToggleComboItem(combo.id, item.id)}
                      className={cn(
                        'w-full flex items-center justify-between px-2 py-1 rounded text-xs transition-colors',
                        isSelected
                          ? 'bg-accent/10 text-accent'
                          : 'text-text-label hover:text-text-primary hover:bg-white/[0.04]',
                      )}
                    >
                      <span>{item.descricao}</span>
                      <span>{formatCurrency(item.total)}</span>
                    </button>
                  )
                })}
              </div>

              {cv && cv.items.length > 0 && (
                <div className="pt-1.5 border-t border-white/[0.06] space-y-0.5">
                  <div className="flex items-center justify-between text-[10px] text-text-label">
                    <span>Subtotal:</span>
                    <span>{formatCurrency(cv.subtotal)}</span>
                  </div>
                  {cv.desconto > 0 && (
                    <div className="flex items-center justify-between text-[10px] text-red-400">
                      <span>Desconto ({cv.desconto}%):</span>
                      <span>-{formatCurrency(cv.descontoValor)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-xs font-bold text-text-primary">
                    <span>Valor do Combo:</span>
                    <span className="text-accent">{formatCurrency(cv.total)}</span>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Pacotes do catálogo (plano + setup, etc.) — não confundir com combo manual */}
      {comboPricingData.length > 0 && (
        <div className="space-y-3 pt-2 border-t border-white/[0.06]">
          <h4 className="text-[11px] font-semibold text-text-label uppercase tracking-wider flex items-center gap-2">
            <Layers size={12} />
            Pacotes (preço conjunto)
          </h4>
          {comboPricingData.map((combo) => (
            <div
              key={combo.groupId}
              className="p-3 rounded-md bg-white/[0.03] border border-white/[0.06] space-y-2"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-text-primary">{combo.label}</span>
                <span className="text-[10px] text-text-label">{combo.items.length} itens</span>
              </div>

              <div className="space-y-1">
                {combo.items.map((item) => (
                  <div key={item.id} className="flex items-center justify-between text-[10px] text-text-label">
                    <span className="truncate max-w-[200px]">{item.descricao}</span>
                    <span>{formatCurrency(item.total)}</span>
                  </div>
                ))}
              </div>

              <div className="pt-1.5 border-t border-white/[0.06] space-y-1">
                <div className="flex items-center justify-between text-[10px] text-text-label">
                  <span>Subtotal:</span>
                  <span>{formatCurrency(combo.subtotal)}</span>
                </div>
                {combo.desconto > 0 && (
                  <div className="flex items-center justify-between text-[10px] text-red-400">
                    <span>Desconto do combo:</span>
                    <span>-{formatCurrency(combo.desconto)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-text-label">Preço final do combo:</span>
                  <div className="flex items-center gap-1">
                    <span className="text-accent text-xs font-medium">
                      {formatCurrency(combo.comboFinalPrice)}
                    </span>
                  </div>
                </div>
              </div>

              {canEdit && (
                <div className="flex items-center gap-2 pt-1">
                  <span className="text-[10px] text-text-label">Ajustar preço:</span>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={combo.comboFinalPrice}
                    onChange={(e) =>
                      onSetComboFinalPrice(combo.groupId, Math.max(0, Number(e.target.value)))
                    }
                    className="w-24 bg-white/[0.04] border border-white/[0.06] text-text-primary text-xs rounded px-2 py-1 outline-none focus:border-accent text-right"
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  )
}
